import { useState, useEffect, useRef } from "react";
import { api } from "@/lib/api";
import { ChatCircleDots, X, PaperPlaneRight, Sparkle, Trash, ClockCounterClockwise } from "@phosphor-icons/react";

export default function ChatAssistant() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [sessionId, setSessionId] = useState(() => localStorage.getItem("fcf_chat_session") || "");
  const [sessions, setSessions] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const endRef = useRef();

  useEffect(() => {
    if (open && sessionId) {
      api.get("/chat/history", { params: { session_id: sessionId } }).then((r) => setMessages(r.data));
    }
  }, [open, sessionId]);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const send = async () => {
    if (!input.trim() || busy) return;
    const userMsg = { role: "user", content: input, ts: new Date().toISOString() };
    setMessages((m) => [...m, userMsg]);
    setBusy(true);
    const text = input;
    setInput("");
    try {
      const { data } = await api.post("/chat/message", { message: text, session_id: sessionId || undefined });
      if (!sessionId) {
        setSessionId(data.session_id);
        localStorage.setItem("fcf_chat_session", data.session_id);
      }
      setMessages((m) => [...m, { role: "assistant", content: data.reply, ts: new Date().toISOString() }]);
    } catch (e) {
      setMessages((m) => [...m, { role: "assistant", content: "Errore: " + (e.response?.data?.detail || e.message), ts: new Date().toISOString() }]);
    } finally { setBusy(false); }
  };

  const newSession = () => {
    setSessionId("");
    setMessages([]);
    localStorage.removeItem("fcf_chat_session");
    setShowHistory(false);
  };

  const loadHistory = async () => {
    const { data } = await api.get("/chat/sessions");
    setSessions(data);
    setShowHistory(true);
  };

  const switchSession = (sid) => {
    setSessionId(sid);
    localStorage.setItem("fcf_chat_session", sid);
    setShowHistory(false);
  };

  const suggestions = [
    "Quali fatture non hanno CUP?",
    "Riepilogo per beneficiario",
    "Fatture senza bonifico associato",
    "Score di conformità attuale",
  ];

  return (
    <>
      <button onClick={() => setOpen(true)}
        className="fixed bottom-6 right-6 w-14 h-14 rounded-full bg-blue-600 text-white shadow-lg hover:bg-blue-700 flex items-center justify-center z-40 transition-transform hover:scale-105"
        data-testid="chat-fab" title="Assistente AI">
        <ChatCircleDots size={26} weight="duotone" />
      </button>

      {open && (
        <div className="fixed inset-0 z-50 flex justify-end bg-slate-900/30 backdrop-blur-sm fade-in" onClick={() => setOpen(false)}
          data-testid="chat-panel">
          <div className="w-full max-w-md bg-white h-full flex flex-col shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <header className="h-16 bg-slate-900 text-white flex items-center justify-between px-4">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-md bg-blue-600 flex items-center justify-center">
                  <Sparkle size={18} weight="fill" />
                </div>
                <div>
                  <div className="font-bold text-sm" style={{ fontFamily: "Chivo" }}>Assistente AI</div>
                  <div className="text-[10px] text-slate-400 uppercase tracking-widest">OpenAI · GPT-5.4</div>
                </div>
              </div>
              <div className="flex items-center gap-1">
                <button onClick={loadHistory} className="w-8 h-8 rounded flex items-center justify-center hover:bg-slate-800" title="Cronologia" data-testid="chat-history-btn">
                  <ClockCounterClockwise size={17} />
                </button>
                <button onClick={newSession} className="w-8 h-8 rounded flex items-center justify-center hover:bg-slate-800" title="Nuova conversazione" data-testid="chat-new-btn">
                  <Trash size={17} />
                </button>
                <button onClick={() => setOpen(false)} className="w-8 h-8 rounded flex items-center justify-center hover:bg-slate-800" data-testid="chat-close">
                  <X size={17} />
                </button>
              </div>
            </header>

            {showHistory ? (
              <div className="flex-1 overflow-y-auto p-4 space-y-2">
                <div className="eyebrow mb-2">Conversazioni precedenti</div>
                {sessions.length === 0 && <div className="text-slate-400 text-sm">Nessuna conversazione</div>}
                {sessions.map((s) => (
                  <button key={s.session_id} onClick={() => switchSession(s.session_id)}
                    className="w-full text-left p-3 rounded-md border border-slate-200 hover:bg-slate-50"
                    data-testid={`chat-session-${s.session_id}`}>
                    <div className="text-xs text-slate-500">{new Date(s.last_ts).toLocaleString("it-IT")} · {s.count} messaggi</div>
                    <div className="text-sm text-slate-800 mt-1 truncate">{s.last_content}</div>
                  </button>
                ))}
                <button onClick={() => setShowHistory(false)} className="text-blue-600 text-sm mt-4">← Torna alla chat</button>
              </div>
            ) : (
              <>
                <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-slate-50" data-testid="chat-messages">
                  {messages.length === 0 && (
                    <div className="text-center py-8">
                      <Sparkle size={36} className="mx-auto text-blue-600 mb-3" weight="duotone" />
                      <div className="text-sm font-medium text-slate-900">Come posso aiutarti oggi?</div>
                      <div className="text-xs text-slate-500 mt-1">Posso interrogare i tuoi dati di rendicontazione</div>
                      <div className="mt-6 space-y-2">
                        {suggestions.map((s) => (
                          <button key={s} onClick={() => setInput(s)}
                            className="w-full text-left px-3 py-2 text-xs bg-white border border-slate-200 rounded-md hover:border-blue-400 hover:bg-blue-50 text-slate-700"
                            data-testid={`chat-suggest-${s.substring(0,10)}`}>
                            {s}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                  {messages.map((m, i) => (
                    <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
                      data-testid={`chat-msg-${m.role}-${i}`}>
                      <div className={`max-w-[85%] px-3.5 py-2.5 rounded-lg text-sm whitespace-pre-wrap ${
                        m.role === "user" ? "bg-blue-600 text-white rounded-br-none" : "bg-white text-slate-800 border border-slate-200 rounded-bl-none"
                      }`}>
                        {m.content}
                      </div>
                    </div>
                  ))}
                  {busy && (
                    <div className="flex justify-start">
                      <div className="bg-white border border-slate-200 rounded-lg rounded-bl-none px-4 py-3">
                        <div className="flex gap-1">
                          <span className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
                          <span className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
                          <span className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
                        </div>
                      </div>
                    </div>
                  )}
                  <div ref={endRef} />
                </div>

                <div className="p-3 border-t border-slate-200 bg-white">
                  <div className="flex items-end gap-2">
                    <textarea rows={1} value={input}
                      onChange={(e) => setInput(e.target.value)}
                      onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                      placeholder="Scrivi un messaggio…"
                      className="flex-1 resize-none rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 max-h-32"
                      data-testid="chat-input" />
                    <button onClick={send} disabled={busy || !input.trim()}
                      className="w-10 h-10 rounded-md bg-slate-900 text-white flex items-center justify-center hover:bg-slate-800 disabled:opacity-40"
                      data-testid="chat-send">
                      <PaperPlaneRight size={17} weight="fill" />
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}
